import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowRight, BadgeCheck, PenLine } from 'lucide-react'
import { env } from '@/lib/env'
import { formatDateTimeCompact } from '@/lib/datetime'
import { fetchAllPages, type ExportColumn } from '@/lib/export'
import { formatMinorCurrency, humanizeEnum } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import {
  TRIP_EDIT_CHANGES,
  TRIP_EDIT_STATUSES,
  type AdminTripEdit,
  type TripEditFilters,
} from '@/types/api'
import { useTableState } from '@/hooks/useTableState'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { DataTable } from '@/components/ui/DataTable'
import { ExportMenu } from '@/components/ui/ExportMenu'
import { FilterBar, FilterSelect } from '@/components/ui/FilterBar'
import { Pagination } from '@/components/ui/Pagination'
import { RowActions, StackedCell } from '@/components/ui/RowActions'
import { SearchInput } from '@/components/ui/SearchInput'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { fetchTripEdits } from './api'
import { describeStatus, diffOf, summariseChanges } from './describe'
import { ReviewTripEditDialog } from './ReviewTripEditDialog'
import { VerifyTripEditPaymentDialog } from './VerifyTripEditPaymentDialog'
import styles from './TripEditsPage.module.css'

/**
 * Opens on what needs a human.
 *
 * `OPEN` is a pseudo-status the server expands into the three states still
 * waiting on somebody. A supervisor arrives at this screen with one question —
 * "what is waiting on me?" — and the full history is one filter change away.
 */
const DEFAULT_FILTERS = {
  search: '',
  status: 'OPEN',
  change: '',
}

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open — needs someone' },
  ...TRIP_EDIT_STATUSES.map((status) => ({ value: status, label: humanizeEnum(status) })),
]

/**
 * Customer requests to change a booking they have already made.
 *
 * The screen exists because a booking used to be fixed the moment it was paid
 * for: a customer whose meeting moved an hour had to ring the office, or cancel
 * and re-book — losing the reference, the promo and, on a card booking, the
 * payment.
 *
 * Two things are worth knowing before using it:
 *
 * - **Nothing here has changed the booking yet.** A request sits beside the
 *   booking rather than on it, so an unanswered one costs nothing and confuses
 *   no driver. Only approving — and, where there is a charge, the customer
 *   paying — moves anything.
 * - **The price is a decision, not a calculation.** Nothing re-prices the trip
 *   from the package, because the reason a change is worth more money is often
 *   not in the data: a longer wait, a harder route, a favour for a regular.
 */
export function TripEditsPage() {
  const table = useTableState({ defaultSort: 'createdAt', defaultFilters: DEFAULT_FILTERS })
  const [reviewing, setReviewing] = useState<AdminTripEdit | null>(null)
  const [verifying, setVerifying] = useState<AdminTripEdit | null>(null)

  useEffect(() => {
    document.title = `Trip changes · ${env.appName} Operations`
  }, [])

  const filters = useMemo<TripEditFilters>(
    () => ({
      page: table.page,
      size: table.size,
      sort: table.sort,
      direction: table.direction,
      search: table.filters.search || undefined,
      status: table.filters.status || undefined,
      change: table.filters.change || undefined,
    }),
    [table.page, table.size, table.sort, table.direction, table.filters],
  )

  const editsQuery = useQuery({
    queryKey: queryKeys.tripEdits.list(filters),
    queryFn: () => fetchTripEdits(filters),
    placeholderData: (previous) => previous,
  })

  const columns = useMemo<ColumnDef<AdminTripEdit, unknown>[]>(
    () => [
      {
        id: 'customer',
        header: 'Customer',
        meta: { sortKey: 'userId', mobileLabel: 'Customer' },
        cell: ({ row }) => (
          <StackedCell
            primary={row.original.customerName ?? `#${row.original.userId}`}
            secondary={row.original.customerPhone ?? undefined}
          />
        ),
      },
      {
        id: 'bookingReference',
        header: 'Booking',
        meta: { sortKey: 'bookingReference', mobileLabel: 'Booking' },
        cell: ({ row }) => (
          <StackedCell
            primary={
              <span style={{ fontFamily: 'var(--font-mono)' }}>
                {row.original.bookingReference ?? `#${row.original.tripRequestId}`}
              </span>
            }
            secondary={formatDateTimeCompact(row.original.bookingStartTime)}
          />
        ),
      },
      {
        /* The whole change in the row, not just its kind. A supervisor scanning
           the queue is deciding what to open next, and "Pickup time" alone does
           not distinguish a ten-minute shift from a different day. */
        id: 'change',
        header: 'Change',
        meta: { mobileLabel: 'Change' },
        cell: ({ row }) => {
          const rows = diffOf(row.original)
          return (
            <div className={styles.cellDiff}>
              {rows.map((diff) => (
                <span key={`${diff.kind}-${diff.label}`} className={styles.cellDiffRow}>
                  <span className={styles.cellDiffLabel}>{diff.label}</span>
                  <span className={styles.diffBefore}>{diff.before}</span>
                  <ArrowRight aria-hidden="true" className={styles.diffArrow} />
                  <span className={styles.diffAfter}>{diff.after}</span>
                </span>
              ))}
            </div>
          )
        },
      },
      {
        id: 'extraAmountMinor',
        header: 'Extra',
        meta: { sortKey: 'extraAmountMinor', mobileLabel: 'Extra', align: 'right' },
        cell: ({ row }) => (
          <StackedCell
            primary={
              row.original.extraAmountMinor > 0
                ? formatMinorCurrency(row.original.extraAmountMinor, row.original.currency)
                : '—'
            }
            secondary={
              row.original.extraAmountMinor > 0
                ? `of ${formatMinorCurrency(row.original.bookingTotalMinor, row.original.currency)} total`
                : undefined
            }
          />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        meta: { sortKey: 'status', mobileLabel: 'Status' },
        cell: ({ row }) => (
          <StackedCell
            primary={<StatusBadge status={row.original.status} />}
            secondary={describeStatus(row.original)}
          />
        ),
      },
      {
        id: 'createdAt',
        header: 'Asked',
        meta: { sortKey: 'createdAt', mobileLabel: 'Asked', hideOnMobile: true },
        cell: ({ row }) => formatDateTimeCompact(row.original.createdAt),
      },
      {
        id: 'actions',
        header: 'Actions',
        meta: { align: 'right', sticky: true, width: '190px' },
        cell: ({ row }) => (
          <RowActions>
            {/* `decidable` and `verifiable` are the server's judgement, not a
                status string re-read here: a change already answered elsewhere
                loses its button on the next refresh rather than offering an
                action that will be refused. */}
            {row.original.decidable ? (
              <Button
                size="sm"
                variant="primary"
                iconLeft={<PenLine aria-hidden="true" />}
                onClick={() => setReviewing(row.original)}
              >
                Review
              </Button>
            ) : null}
            {row.original.verifiable ? (
              <Button
                size="sm"
                variant="primary"
                iconLeft={<BadgeCheck aria-hidden="true" />}
                onClick={() => setVerifying(row.original)}
              >
                Check transfer
              </Button>
            ) : null}
          </RowActions>
        ),
      },
    ],
    [],
  )

  const exportColumns = useMemo<ExportColumn<AdminTripEdit>[]>(
    () => [
      { header: 'ID', value: (row) => row.id },
      { header: 'Booking', value: (row) => row.bookingReference ?? '' },
      { header: 'Customer', value: (row) => row.customerName ?? '' },
      { header: 'Customer phone', value: (row) => row.customerPhone ?? '' },
      { header: 'Status', value: (row) => row.status },
      { header: 'Changes', value: (row) => summariseChanges(row) },
      { header: 'New pickup time', value: (row) => formatDateTimeCompact(row.requestedStartTime, '') },
      { header: 'Previous pickup time', value: (row) => formatDateTimeCompact(row.previousStartTime, '') },
      { header: 'New duration', value: (row) => row.requestedPackageName ?? '' },
      { header: 'Previous duration', value: (row) => row.previousPackageName ?? '' },
      { header: 'New pickup', value: (row) => row.requestedPickupAddress ?? '' },
      { header: 'New destination', value: (row) => row.requestedDropoffAddress ?? '' },
      { header: 'Customer note', value: (row) => row.customerNote ?? '' },
      // Major units, so a finance spreadsheet can sum the column directly.
      { header: 'Extra charge', value: (row) => (row.extraAmountMinor / 100).toFixed(2) },
      { header: 'Trip total', value: (row) => (row.bookingTotalMinor / 100).toFixed(2) },
      { header: 'Currency', value: (row) => row.currency },
      { header: 'Decided by', value: (row) => row.decidedByAdminName ?? '' },
      { header: 'Decided', value: (row) => formatDateTimeCompact(row.decidedAt, '') },
      { header: 'Decision note', value: (row) => row.decisionNote ?? '' },
      { header: 'Paid by', value: (row) => row.paymentMethod ?? '' },
      { header: 'Paid', value: (row) => formatDateTimeCompact(row.paidAt, '') },
      { header: 'Verified by', value: (row) => row.verifiedByAdminName ?? '' },
      { header: 'Asked', value: (row) => formatDateTimeCompact(row.createdAt, '') },
    ],
    [],
  )

  const page = editsQuery.data
  const waitingCount = page?.content.filter((edit) => edit.decidable || edit.verifiable).length ?? 0

  return (
    <>
      <PageHeader
        title="Trip changes"
        description="Customers asking to move a booking's time, duration or location. Nothing here has changed a booking yet — approving does, and where there is an extra charge, only the customer's payment does."
        meta={
          page ? (
            <>
              <span>{page.totalElements} requests</span>
              {waitingCount > 0 ? <span>{waitingCount} waiting on you on this page</span> : null}
            </>
          ) : null
        }
      />

      <Card flush>
        <FilterBar
          search={
            <SearchInput
              value={table.filters.search}
              onChange={(value) => table.setFilter('search', value)}
              placeholder="Search booking reference, note or address…"
              label="Search trip changes"
            />
          }
          actions={
            <ExportMenu
              columns={exportColumns}
              baseName="drive-for-me-trip-changes"
              title="Drive For Me — Trip changes"
              fetchRows={() =>
                fetchAllPages((exportPage, exportSize) =>
                  fetchTripEdits({ ...filters, page: exportPage, size: exportSize }),
                )
              }
            />
          }
          activeFilterCount={table.activeFilterCount}
          onReset={table.resetFilters}
        >
          <FilterSelect
            label="Status"
            value={table.filters.status}
            onChange={(value) => table.setFilter('status', value)}
            options={STATUS_OPTIONS}
          />
          <FilterSelect
            label="Changes"
            value={table.filters.change}
            onChange={(value) => table.setFilter('change', value)}
            options={TRIP_EDIT_CHANGES.map((change) => ({ value: change, label: humanizeEnum(change) }))}
          />
        </FilterBar>

        <DataTable
          caption="Drive For Me customer trip changes"
          data={page?.content ?? []}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={editsQuery.isLoading}
          isFetching={editsQuery.isFetching && !editsQuery.isLoading}
          error={editsQuery.isError ? editsQuery.error : undefined}
          onRetry={() => void editsQuery.refetch()}
          sort={table.sort}
          direction={table.direction}
          onSortChange={table.setSort}
          emptyTitle={
            table.filters.status === 'OPEN' ? 'Nothing is waiting on you' : 'No trip changes match these filters'
          }
          emptyDescription={
            table.filters.status === 'OPEN'
              ? 'A request appears here as soon as a customer asks to move a booking. Change the status filter to see ones that have already been answered.'
              : 'Try a different status, or clear the filters.'
          }
          emptyAction={
            table.activeFilterCount > 0 ? (
              <Button variant="secondary" onClick={table.resetFilters}>
                Clear filters
              </Button>
            ) : null
          }
        />

        {page && page.totalElements > 0 ? (
          <Pagination
            page={page.page}
            size={page.size}
            totalElements={page.totalElements}
            totalPages={page.totalPages}
            onPageChange={table.setPage}
            onSizeChange={table.setSize}
            noun="request"
            pluralNoun="requests"
            disabled={editsQuery.isFetching}
          />
        ) : null}
      </Card>

      <ReviewTripEditDialog edit={reviewing} onClose={() => setReviewing(null)} />
      <VerifyTripEditPaymentDialog edit={verifying} onClose={() => setVerifying(null)} />
    </>
  )
}
